import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useQuestionnaireStore } from "../../store/useQuestionnaireStore";
import { useClientStore } from "../../store/useClientStore";
import { useQuestionnaireTemplateStore } from "../../store/useQuestionnaireTemplateStore";
import { useProjectTypeStore } from "../../store/useProjectTypeStore";

import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import Button from "../ui/Button";
import QuestionnairePreview from "./QuestionnairePreview";
import { Eye, Sparkles, Loader2 } from "lucide-react";
import { axiosInstance } from "../../lib/axios";

interface Props {
  onSuccess: () => void;
}

const QuestionnaireForm = ({ onSuccess }: Props) => {
  const { createQuestionnaire, isCreating } = useQuestionnaireStore();
  const { clients, fetchClients } = useClientStore();
  const { templates, fetchTemplates } = useQuestionnaireTemplateStore();
  const { types, fetchTypes } = useProjectTypeStore();

  const [showPreview, setShowPreview] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [aiDesc, setAiDesc] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    serviceType: "",
    clientId: "",
    layoutType: "form" as "form" | "card",
    questions: [
  { question: "", answerType: "text", options: [""], answer: "", required: false, placeholder: "" },
],

  });

  const answerTypeOptions = [
    { value: "text", label: "Text" },
    { value: "date", label: "Date" },
    { value: "radio", label: "Radio Buttons" },
    { value: "checkbox", label: "Checkboxes" },
    { value: "select", label: "Dropdown Select" },
  ];

  // Service types come from the admin-managed project types, so this list stays
  // in step with Projects instead of drifting from a hardcoded copy.
  const projectTypeOptions = [
    { value: "", label: "Select a service type…" },
    ...types.map((t) => ({ value: t.name, label: t.name })),
  ];

  // A questionnaire is created for a WON contact (before any project exists),
  // so only Won contacts / Clients are selectable.
  const wonClients = clients.filter(
    (c: any) => c.contactStatus === "Client" || c.leadStatus === "Won Lead"
  );
  const clientOptions = [
    { value: "", label: "Select a won contact…" },
    ...wonClients.map((c: any) => ({ value: c._id, label: c.username ? `${c.name} · @${c.username}` : c.name })),
  ];

  useEffect(() => {
    fetchClients();
    fetchTemplates();
    fetchTypes();
  }, [fetchClients, fetchTemplates, fetchTypes]);

  // ── Answer types ──────────────────────────────────────────────────────────
  //
  // Every type QuestionInput can render, which is what the client actually
  // sees. The type is passed through untouched; only something genuinely
  // unknown falls back to a text box.
  //
  // This used to collapse everything to text/radio/checkbox, and that one line
  // is what turned the Logo and Print intakes back into a plain form: the
  // described option cards, the opposing-pair sliders and the long-answer boxes
  // all have their own renderers, and all three were being thrown away on the
  // way in.
  const RICH_TYPES = ["cards", "cards-multi", "slider-pairs"];
  const SUPPORTED_TYPES = [
    "text", "date", "textarea", "radio", "checkbox", "select",
    "longtext", "number", "email", "phone", "singleselect", "multiselect",
    ...RICH_TYPES,
  ];

  const mapType = (t: string) => {
    const type = String(t || "text");
    if (SUPPORTED_TYPES.includes(type)) return type;
    // "file" and anything else a template might carry that has no renderer.
    return "text";
  };

  // Apply a template: copy its questions (editable) + prefill name/service type.
  //
  // Everything the question carries is copied, not just the five fields the
  // basic editor shows. A template's sections, its per-option descriptions, its
  // slider pairs and its conditional rules are the template — dropping them on
  // the way in produced a questionnaire that had the right QUESTIONS and none
  // of the structure that made them worth asking that way.
  const applyTemplate = (id: string) => {
    setTemplateId(id);
    if (!id) return;
    const tpl = templates.find((t) => t._id === id);
    if (!tpl) return;

    const questions = tpl.questions.map((q: any) => ({
      question: q.question,
      answerType: mapType(q.answerType),
      options: q.options?.length ? [...q.options] : [""],
      answer: "",
      required: q.required,
      placeholder: q.placeholder || "",

      // Grouping and guidance.
      section: q.section || "",
      sectionTitle: q.sectionTitle || "",
      sectionIntro: q.sectionIntro || "",
      helpText: q.helpText || "",
      // Described option cards, opposing-pair sliders.
      optionDetails: q.optionDetails?.length ? q.optionDetails.map((o: any) => ({ ...o })) : [],
      pairs: q.pairs?.length ? q.pairs.map((p: any) => ({ ...p })) : [],
      // Conditional display — without the key, nothing can point at it.
      showWhen: q.showWhen?.questionKey
        ? { questionKey: q.showWhen.questionKey, anyOf: [...(q.showWhen.anyOf || [])] }
        : undefined,
      key: q.key || "",
      order: q.order || 0,
    }));

    // A template built as a guided intake is shown as one, unless the person
    // deliberately changes it afterwards.
    const isGuided = questions.some(
      (q) => RICH_TYPES.includes(q.answerType) || q.sectionTitle || q.section
    );

    setFormData((prev) => ({
      ...prev,
      name: prev.name || tpl.name,
      serviceType: tpl.serviceType || prev.serviceType,
      layoutType: isGuided ? "card" : prev.layoutType,
      questions,
    }));
  };

  // Generate questions with AI from a project / job description.
  const generateAI = async () => {
    const desc = (aiDesc || formData.description || "").trim();
    if (!desc) {
      toast.error("Paste a project / job description first.");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await axiosInstance.post("/questionnaire/ai-generate", {
        description: desc,
        serviceType: formData.serviceType,
      });
      const qs = (data.questions || []).map((q: any) => ({
        question: q.question || "",
        answerType: q.answerType || "text",
        options: Array.isArray(q.options) && q.options.length ? q.options : [""],
        answer: "",
        required: q.required !== false,
        placeholder: q.placeholder || "",
      }));
      if (!qs.length) {
        toast.error("Couldn't generate questions. Try a longer description.");
        return;
      }
      setFormData((prev) => ({ ...prev, questions: qs }));
      toast.success(data.aiGenerated ? `Generated ${qs.length} questions with AI` : `Added ${qs.length} starter questions`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated: any = [...formData.questions];
    updated[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      questions: updated,
    }));
  };

  const toggleRequired = (index: number) => {
    const updated: any = [...formData.questions];
    updated[index].required = !updated[index].required;
    setFormData((prev) => ({
      ...prev,
      questions: updated,
    }));
  };

  const addQuestionOption = (index: number) => {
    const updated: any = [...formData.questions];
    updated[index].options.push("");
    setFormData((prev) => ({
      ...prev,
      questions: updated,
    }));
  };

  const updateQuestionOption = (
    qIndex: number,
    optIndex: number,
    value: string
  ) => {
    const updated: any = [...formData.questions];
    updated[qIndex].options[optIndex] = value;
    setFormData((prev) => ({
      ...prev,
      questions: updated,
    }));
  };

  const addQuestion = () => {
    setFormData((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { question: "", answerType: "text", options: [""], answer: "", required: false , placeholder:""},
      ],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) {
      toast.error("Select the won contact this questionnaire is for.");
      return;
    }

    await createQuestionnaire({
      name: formData.name,
      description: formData.description,
      clientId: formData.clientId,
      serviceType: formData.serviceType,
      layoutType: formData.layoutType,
      questions: formData.questions,
    });

    onSuccess();
  };

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-8">

      {/* Start from a prebuilt template (questions copy in, fully editable) */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
        <label className="block text-sm font-semibold text-gray-800 mb-1.5">Start from a Pre-Built Template</label>
        <select
          value={templateId}
          onChange={(e) => applyTemplate(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] outline-none"
        >
          <option value="">— Blank (build from scratch) —</option>
          {templates.map((t) => (
            <option key={t._id} value={t._id}>{t.category} · {t.name}</option>
          ))}
        </select>
        <p className="text-[11px] text-gray-500 mt-1.5">Loads the template's questions (Contact + Requirements + Future Services), which you can edit before sharing.</p>
      </div>

      {/* Generate with AI from a project / job description */}
      <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Sparkles size={15} className="text-violet-600" />
          <label className="block text-sm font-semibold text-gray-800">Generate with AI</label>
        </div>
        <textarea
          value={aiDesc}
          onChange={(e) => setAiDesc(e.target.value)}
          rows={3}
          placeholder="Paste the job description or project description here — AI will generate relevant questions…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-violet-400 outline-none"
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-[11px] text-gray-500">Generated questions replace the list below and stay fully editable.</p>
          <button
            type="button"
            onClick={generateAI}
            disabled={aiLoading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 disabled:opacity-50"
          >
            {aiLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate Questions
          </button>
        </div>
      </div>

      {/* Details */}
      <section className="space-y-4">
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Details</h3>

        <InputField
          label="Questionnaire Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g. Website Discovery — Acme Corp"
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Briefly describe what this questionnaire is for…"
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] hover:border-[#2462b2]/50 transition-all"
            rows={3}
          />
        </div>

        <SelectField
          label="Service Type"
          name="serviceType"
          value={formData.serviceType}
          onChange={handleChange}
          options={projectTypeOptions}
          required
        />
      </section>

      {/* Layout Type — how the questionnaire is presented to the client. */}
      <section className="space-y-3">
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Layout Type</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { value: "form", title: "Form Layout", desc: "Classic single page — all questions stacked. (Default)" },
            { value: "card", title: "Card Layout", desc: "Modern step-by-step wizard — one question per card." },
          ].map((opt) => (
            <button
              type="button"
              key={opt.value}
              onClick={() => setFormData((prev) => ({ ...prev, layoutType: opt.value as "form" | "card" }))}
              className={`text-left rounded-xl border-2 p-4 transition-all ${
                formData.layoutType === opt.value
                  ? "border-[#2462b2] bg-blue-50/60 ring-2 ring-[#2462b2]/20"
                  : "border-gray-200 hover:border-[#2462b2]/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-bold text-gray-800">{opt.title}</span>
                <span
                  className={`w-4 h-4 rounded-full border-2 ${
                    formData.layoutType === opt.value ? "border-[#2462b2] bg-[#2462b2]" : "border-gray-300"
                  }`}
                />
              </div>
              <p className="text-[12px] text-gray-500 mt-1">{opt.desc}</p>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-500">Use the Preview button below to compare both layouts before sharing.</p>
      </section>

      {/* Linking — a questionnaire belongs to a WON contact. It gets linked to a
          project later, when the project is created. */}
      <section className="space-y-2">
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Won Contact</h3>
        <SelectField
          label="Contact"
          name="clientId"
          value={formData.clientId}
          onChange={handleChange}
          options={clientOptions}
          required
        />
        <p className="text-[11px] text-gray-500">
          This questionnaire is linked to the contact now. You'll link it to a project later, when the project is created.
        </p>
      </section>

      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Questions</h3>
          <span className="text-[11px] font-semibold text-gray-400">{formData.questions.length} total</span>
        </div>

        {formData.questions.map((q, i) => (
          <div key={i} className="p-4 bg-gray-50 border rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-700">Question {i + 1}</h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={q.required}
                  onChange={() => toggleRequired(i)}
                  className="w-4 h-4"
                />
                Required
              </label>
            </div>

            <InputField
              label="Question Text"
              name={`question-${i}`}
              value={q.question}
              onChange={(e) => updateQuestion(i, "question", e.target.value)}
              placeholder="Enter question text"
              required
            />

            <InputField
  label="Placeholder (Optional)"
  name={`placeholder-${i}`}
  value={q.placeholder}
  onChange={(e) => updateQuestion(i, "placeholder", e.target.value)}
  placeholder="Enter input placeholder text"
/>

            <SelectField
              label="Answer Type"
              name={`answerType-${i}`}
              value={q.answerType}
              onChange={(e) => updateQuestion(i, "answerType", e.target.value)}
              options={answerTypeOptions}
            />

            {["radio", "checkbox", "select"].includes(q.answerType) && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Options
                </label>
                {q.options.map((opt: string, optIndex: number) => (
                  <InputField
                    key={optIndex}
                    label={`Option ${optIndex + 1}`}
                    name={`option-${i}-${optIndex}`}
                    value={opt}
                    onChange={(e) =>
                      updateQuestionOption(i, optIndex, e.target.value)
                    }
                    placeholder="Enter option value"
                  />
                ))}

                <button
                  type="button"
                  onClick={() => addQuestionOption(i)}
                  className="text-[#2462b2] font-bold hover:underline"
                >
                  + Add Option
                </button>
              </div>
            )}

            <InputField
              label="Default Answer (Optional)"
              name={`answer-${i}`}
              value={q.answer}
              onChange={(e) => updateQuestion(i, "answer", e.target.value)}
              placeholder="Default answer"
            />
          </div>
        ))}

        <button
          type="button"
          onClick={addQuestion}
          className="text-[#2462b2] font-bold hover:underline"
        >
          + Add Another Question
        </button>
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-[#2462b2] text-[#2462b2] font-bold rounded-xl px-6 py-3 hover:bg-[#2462b2] hover:text-white transition-all duration-200"
        >
          <Eye size={20} />
          Preview Questionnaire
        </button>
        <Button type="submit" isLoading={isCreating} className="flex-1">
          {isCreating ? "Creating..." : "Create Questionnaire"}
        </Button>
      </div>
    </form>

    {showPreview && (
      <QuestionnairePreview
        formData={formData}
        layout={formData.layoutType}
        onClose={() => setShowPreview(false)}
      />
    )}
    </>
  );
};

export default QuestionnaireForm;
